import * as React from 'react';
import { ParityOffer, ParityOfferMode } from 'constants/payloads/parityOffer';
import { getParityOfferMode } from 'utils/shared/parityOffers';
import { useGeo } from 'context/GeoContext';

/**
 * The regional offer is now a thin selector over the single geo lookup (context/GeoContext).
 * There is no separate fetch or provider here anymore — GeoProvider makes one call per
 * session and this derives the offer from it.
 *
 * Rollback stays reliable because the mode is read from the build-time-inlined
 * PARITY_OFFER_MODE (getParityOfferMode), NOT from the geo response: if the geo lookup is the
 * unhealthy subsystem, a rollback still engages. The geo response only supplies the offer
 * DATA (which country, which code).
 */

export type ParityOfferLookupStatus = 'loading' | 'success' | 'error';

interface ParityOfferView {
  status: ParityOfferLookupStatus;
  provider: ParityOfferMode;
  offer: ParityOffer | null;
}

/**
 * One-release-cycle rollback path: `PARITY_OFFER_MODE=paritydeals` re-injects the third-party
 * script and the native offer stays out of the way, restoring the previous behavior exactly.
 *
 * Rendered from `layouts/Website`, NOT app-wide, so the legacy script loads only on the
 * marketing pages it used to load on — never on the paid dashboard.
 */
export const LegacyParityDealsLoader = () => {
  React.useEffect(() => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[data-insiderfinance-paritydeals]',
    );
    if (existingScript) return;

    const script = document.createElement('script');
    script.src = 'https://cdn.paritydeals.com/banner.js';
    script.async = true;
    script.dataset.insiderfinanceParitydeals = 'true';
    document.head.appendChild(script);

    return () => script.remove();
  }, []);

  return null;
};

export const useParityOffer = (): ParityOfferView => {
  const { status, parity } = useGeo();
  return { status, provider: getParityOfferMode(), offer: parity.offer };
};

/**
 * The offer to show, or null. A regional offer replaces the site-wide sale when the visitor
 * is eligible: every sale is a one-time discount, the regional offer is forever. Returns null
 * until the lookup succeeds and after any failure, so the sale renders during and after a
 * failed lookup.
 */
export const useActiveParityOffer = (): ParityOffer | null => {
  const { status, parity } = useGeo();
  return getParityOfferMode() === 'custom' && status === 'success' ? parity.offer : null;
};
